import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';

export const OrganogramEditModal = ({
  isOpen,
  position,
  allPositions = [],
  onSave,
  onClose,
  isSubmitting = false,
  errorMessage = null,
}) => {
  const [formData, setFormData] = useState({
    positionName: '',
    positionDescription: '',
    parentPositionId: '',
  });

  useEffect(() => {
    if (position) {
      setFormData({
        positionName: position.positionName || '',
        positionDescription: position.positionDescription || '',
        parentPositionId: position.parentPositionId || '',
      });
    } else {
      setFormData({
        positionName: '',
        positionDescription: '',
        parentPositionId: '',
      });
    }
  }, [position, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.positionName.trim()) {
      alert('Position name is required');
      return;
    }

    if (!formData.positionDescription.trim()) {
      alert('Position description is required');
      return;
    }

    onSave({
      ...formData,
      positionName: formData.positionName.trim(),
      positionDescription: formData.positionDescription.trim(),
    });
  };

  if (!isOpen) return null;

  // Get available parent positions (exclude self and its children for edit)
  const getAvailableParents = () => {
    return allPositions.filter((pos) => {
      // Don't allow self as parent
      if (position && pos.id === position.id) return false;
      // Don't allow children as parents (in edit mode)
      if (position) {
        // In a real app, you'd check if pos is a descendant of position
        return true;
      }
      return true;
    });
  };

  const availableParents = getAvailableParents();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {position ? 'Edit Position' : 'Create Position'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors">
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          <div>
            <label htmlFor="positionName" className="block text-sm font-medium text-gray-900 mb-1">
              Position Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="positionName"
              name="positionName"
              value={formData.positionName}
              onChange={handleChange}
              placeholder="e.g., Manager, CEO, Developer"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSubmitting}
              maxLength="120"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.positionName.length}/120 characters
            </p>
          </div>

          <div>
            <label htmlFor="positionDescription" className="block text-sm font-medium text-gray-900 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="positionDescription"
              name="positionDescription"
              value={formData.positionDescription}
              onChange={handleChange}
              placeholder="Describe the responsibilities and requirements of this position"
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSubmitting}
              maxLength="300"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.positionDescription.length}/300 characters
            </p>
          </div>

          <div>
            <label htmlFor="parentPositionId" className="block text-sm font-medium text-gray-900 mb-1">
              Reports to (Parent Position)
            </label>
            <select
              id="parentPositionId"
              name="parentPositionId"
              value={formData.parentPositionId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              <option value="">-- Root Position (No Parent) --</option>
              {availableParents.map((pos) => (
                <option key={pos.id} value={pos.id}>
                  {pos.positionName}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Select who this position reports to in the hierarchy
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Position'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
